import React, { useState } from "react";
import "./css/Pitch.css";
import PlayerModal from "./PlayerModal";

/**
 * A modal specifically for the formation page that lets you assign
 * a player to a specific position.
 */
const FormationModal = ({
  isOpen,
  onClose,
  positionIndex,
  players,
  currentAssignments,
  onPositionAssignmentChange,
}) => {
  if (!isOpen) return null;

  // Extract which players are already assigned to other positions
  const assignedPlayers = Object.entries(currentAssignments).reduce(
    (acc, [posIdx, playerId]) => {
      acc[playerId] = posIdx;
      return acc;
    },
    {}
  );
  const shadeColor = (color, percent) => {
    let R = parseInt(color.substring(1, 3), 16);
    let G = parseInt(color.substring(3, 5), 16);
    let B = parseInt(color.substring(5, 7), 16);

    R = parseInt((R * (100 + percent)) / 100);
    G = parseInt((G * (100 + percent)) / 100);
    B = parseInt((B * (100 + percent)) / 100);

    R = R < 255 ? R : 255;
    G = G < 255 ? G : 255;
    B = B < 255 ? B : 255;

    const RR =
      R.toString(16).length === 1 ? "0" + R.toString(16) : R.toString(16);
    const GG =
      G.toString(16).length === 1 ? "0" + G.toString(16) : G.toString(16);
    const BB =
      B.toString(16).length === 1 ? "0" + B.toString(16) : B.toString(16);

    return "#" + RR + GG + BB;
  };

  // Utility function to determine text color based on background
  const getContrastColor = (bgColor) => {
    if (!bgColor) return "#FFFFFF";
    const color = bgColor.substring(1);
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  };

  const handlePlayerSelect = (playerId) => {
    const currentPlayerPosition = assignedPlayers[playerId];

    if (currentPlayerPosition !== undefined) {
      // Swap assignments
      onPositionAssignmentChange(positionIndex, playerId);
      onPositionAssignmentChange(parseInt(currentPlayerPosition, 10), null);
    } else {
      // Assign player to position
      onPositionAssignmentChange(positionIndex, playerId);
    }

    onClose();
  };

  return (
    <div className="formation-modal-backdrop">
      <div className="formation-modal-content">
        {/* Header with close button on the left */}
        <div className="formation-modal-header">
          <button
            className="formation-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="fas fa-times"></i>
          </button>
          <h2 className="formation-modal-title">Assign Player</h2>
        </div>

        {/* Body with scrollable player list in two columns */}
        <div className="formation-modal-body">
          <p className="formation-modal-instruction">
            Tap a player below to assign to this position.
          </p>
          <div className="formation-modal-player-list">
            {players.map((player) => {
              const isAssignedElsewhere =
                assignedPlayers[player.player_id] !== undefined;

              return (
                <button
                  key={player.player_id}
                  className={`formation-modal-player-item ${
                    isAssignedElsewhere ? "assigned" : ""
                  }`}
                  onClick={() => handlePlayerSelect(player.player_id)}
                >
                  {player.player_name}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const Pitch = ({
  formationPositions,
  positionAssignments,
  players = [],
  coordinates,
  markerColor,
  gkColor,
  isPressToTalk = false,
  isFormationPage = false,
  onPositionAssignmentChange,
  setPlayerStats,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // For formation assignment
  const [formationModalOpen, setFormationModalOpen] = useState(false);
  const [clickedPositionIndex, setClickedPositionIndex] = useState(null);

  // If you are using PressToTalk stats:
  const updatePlayerStat = (playerId, statKey, increment) => {
    if (!setPlayerStats) return;
    setPlayerStats((prevStats) =>
      prevStats.map((p) =>
        p.player_id === playerId
          ? { ...p, [statKey]: Math.max(p[statKey] + increment, 0) }
          : p
      )
    );
  };

  const getContrastColor = (bgColor) => {
    const color = bgColor.substring(1);
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? "#000000" : "#FFFFFF";
  };

  /**
   * When a marker is clicked:
   *  - If isFormationPage = true, open FormationModal to pick a player.
   *  - If isPressToTalk = true, open PlayerModal to update stats.
   */
  const handleMarkerClick = (positionIndex) => {
    if (isFormationPage) {
      setClickedPositionIndex(positionIndex);
      setFormationModalOpen(true);
    } else if (isPressToTalk) {
      // Ensure the clicked position index is recorded:
      setClickedPositionIndex(positionIndex);
      const playerId = positionAssignments[positionIndex];
      const player = players.find((p) => p.player_id === playerId);
      if (player) {
        setSelectedPlayer(player);
        setModalVisible(true);
      }
    }
  };

  return (
    <div className="pitch-container">
      <div className="pitch">
        {formationPositions.map((position, index) => {
          const playerId = positionAssignments[index];
          const player = players.find((p) => p.player_id === playerId);
          const coord = coordinates[index];
          if (!coord) return null;

          // If it's GK, use gkColor; otherwise markerColor
          const playerColor = position === "GK" ? gkColor : markerColor;
          const textColor = getContrastColor(playerColor);

          // Conditionally add a border if no player is assigned
          const borderStyle = !player
            ? "2px dashed red" // e.g., a dashed red border for an unassigned position
            : "none"; // or no border if a player is assigned

          return (
            <div
              style={{
                position: "absolute",
                top: `${coord.top}%`,
                left: `${coord.left}%`,
                transform: "translate(-50%, -50%)",
                width: "40px", // Reduced size for smaller shirt
                height: "40px",
                cursor: "pointer",
                filter: !player ? "brightness(0.7)" : "none",
                WebkitFilter: !player ? "brightness(0.7)" : "none",
              }}
              onClick={() => handleMarkerClick(index)}
            >
              <svg
                viewBox="0 0 450 400"
                width="100%"
                height="100%"
                style={{ display: "block" }}
              >
                <path
                  d="M350.657,395.571H95.919c-3.294,0-5.967-2.667-5.967-5.967V192.596l-19.285,19.285    
      c-2.238,2.238-6.2,2.238-8.437,0L1.75,151.407c-2.333-2.333-2.333-6.104,0-8.437l89.378-89.378    
      c1.086-1.539,2.834-2.584,4.797-2.584h60.737c3.294,0,5.967,2.673,5.967,5.967c0,33.45,27.215,60.665,60.665,60.665    
      s60.665-27.215,60.665-60.665c0-3.294,2.667-5.967,5.967-5.967h60.737c2.178,0,4.087,1.17,5.126,2.912l89.044,89.05    
      c1.122,1.122,1.748,2.637,1.748,4.219s-0.627,3.103-1.748,4.219l-60.486,60.474c-2.333,2.333-6.104,2.333-8.437,0l-19.285-19.285    
      v197.008C356.623,392.904,353.95,395.571,350.657,395.571z"
                  fill={player ? playerColor : "#FFFFFF"} // Shirt color
                  stroke="black" // Border color
                  strokeWidth="20" // Thicker border
                />
              </svg>

              {/* Player name below the shirt */}
              <div
                style={{
                  position: "absolute",
                  top: "38px", // Adjusted for the smaller shirt
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontSize: "10px", // Reduced font size to match shirt size
                  color: "#444",
                  textAlign: "center",
                  width: "max-content",
                  opacity: !player ? 0.7 : 1,
                }}
              >
                {player ? player.player_name : position}
              </div>
            </div>
          );
        })}
      </div>

      {/* Player Stats Modal (PressToTalk mode) */}
      {modalVisible && isPressToTalk && (
        <PlayerModal
          player={selectedPlayer}
          isOpen={modalVisible}
          onClose={() => setModalVisible(false)}
          updatePlayerStat={updatePlayerStat}
          playerStats={players} // or your state variable holding playerStats
          currentAssignments={positionAssignments}
          onPositionAssignmentChange={onPositionAssignmentChange}
          players={players}
          positionIndex={clickedPositionIndex} // This must not be null!
          setPlayerStats={setPlayerStats}
          formationPositions={formationPositions} // Ensure formationPositions is passed!
        />
      )}

      {/* Formation Modal (Formation page mode) */}
      <FormationModal
        isOpen={formationModalOpen && isFormationPage}
        onClose={() => setFormationModalOpen(false)}
        positionIndex={clickedPositionIndex}
        players={players}
        currentAssignments={positionAssignments}
        onPositionAssignmentChange={onPositionAssignmentChange}
      />
    </div>
  );
};

export default Pitch;
